import React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import toast from 'react-hot-toast'
import { formatDate, formatTime } from '@/utils/formatters'

const meetingSchema = z.object({
  personName: z.string().optional(),
  companyName: z.string().optional(),
  meetingDate: z.string().min(1, 'Data spotkania jest wymagana'),
  meetingTime: z.string().min(1, 'Godzina spotkania jest wymagana'),
  location: z.string().min(1, 'Lokalizacja jest wymagana'),
  title: z.string().optional(),
  description: z.string().optional(),
})

export type MeetingFormData = z.infer<typeof meetingSchema>

interface MeetingFormProps {
  onSubmit: (meetingId: number, data: MeetingFormData) => void
  initialData?: {
    personName?: string | null
    companyName?: string | null
    position?: string | null
    phone?: string | null
    email?: string | null
    website?: string | null
    address?: string | null
  } | null
  businessCardImage?: string | null
}

export const MeetingForm: React.FC<MeetingFormProps> = ({
  onSubmit,
  initialData = null,
  businessCardImage = null
}) => {
  const [isLoading, setIsLoading] = React.useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<MeetingFormData>({
    resolver: zodResolver(meetingSchema),
    defaultValues: {
      personName: initialData?.personName || '',
      companyName: initialData?.companyName || '',
      meetingDate: formatDate(new Date()),
      meetingTime: formatTime(new Date()),
      location: '', // Location should be filled manually, not from business card address
      title: '',
      description: '', // Business card data is stored in the card note, not in meeting description
    },
  })

  const onSubmitForm = async (data: MeetingFormData) => {
    setIsLoading(true)
    try {
      // Create meeting with personName and companyName
      const response = await fetch('/api/meetings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          personName: data.personName,
          companyName: data.companyName,
          meetingDate: new Date(data.meetingDate + 'T' + data.meetingTime).toISOString(),
          meetingTime: data.meetingTime,
          location: data.location,
          title: data.title,
          description: data.description
        })
      })

      if (!response.ok) {
        throw new Error('Failed to create meeting')
      }

      const result = await response.json()
      const meetingId = result.meeting.id

      // If we have business card image, save it as a note
      if (businessCardImage) {
        try {
          await fetch('/api/notes/business-card', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({
              meetingId,
              imageBase64: businessCardImage
            })
          })
        } catch (error) {
          console.error('Failed to save business card image:', error)
          // Don't fail the whole operation if image save fails
        }
      }

      toast.success('Spotkanie zostało utworzone')
      onSubmit(meetingId, data)
    } catch (error) {
      toast.error('Błąd podczas tworzenia spotkania')
      console.error('Meeting creation error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
      {initialData && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
          <p className="text-sm text-green-800 font-medium">
            ✓ Dane z wizytówki zostały automatycznie wypełnione
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="personName">Imię i nazwisko</Label>
          <Input
            id="personName"
            type="text"
            placeholder="np. Jan Kowalski"
            {...register('personName')}
            disabled={isLoading}
          />
          {errors.personName && (
            <p className="text-sm text-red-500">{errors.personName.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="companyName">Nazwa firmy</Label>
          <Input
            id="companyName"
            type="text"
            placeholder="np. Firma ABC Sp. z o.o."
            {...register('companyName')}
            disabled={isLoading}
          />
          {errors.companyName && (
            <p className="text-sm text-red-500">{errors.companyName.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="title">Tytuł spotkania (opcjonalnie)</Label>
        <Input
          id="title"
          type="text"
          placeholder="np. Spotkanie z klientem XYZ"
          {...register('title')}
          disabled={isLoading}
        />
        {errors.title && (
          <p className="text-sm text-red-500">{errors.title.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="meetingDate">Data spotkania</Label>
          <Input
            id="meetingDate"
            type="date"
            {...register('meetingDate')}
            disabled={isLoading}
          />
          {errors.meetingDate && (
            <p className="text-sm text-red-500">{errors.meetingDate.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="meetingTime">Godzina spotkania</Label>
          <Input
            id="meetingTime"
            type="time"
            {...register('meetingTime')}
            disabled={isLoading}
          />
          {errors.meetingTime && (
            <p className="text-sm text-red-500">{errors.meetingTime.message}</p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="location">Lokalizacja</Label>
        <Input
          id="location"
          type="text"
          placeholder="np. Biuro, Restauracja XYZ"
          {...register('location')}
          disabled={isLoading}
        />
        {errors.location && (
          <p className="text-sm text-red-500">{errors.location.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Opis (opcjonalnie)</Label>
        <Textarea
          id="description"
          placeholder="Dodatkowe informacje o spotkaniu..."
          {...register('description')}
          disabled={isLoading}
          rows={3}
        />
        {errors.description && (
          <p className="text-sm text-red-500">{errors.description.message}</p>
        )}
      </div>

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? 'Tworzenie...' : 'Utwórz spotkanie i przejdź do notatek'}
      </Button>
    </form>
  )
}
